/**
 * WhyWeBuildPage — the long-form manifesto explaining the philosophy
 * behind SocialPulse, SocialCapital.cc and the sister sites. Linked
 * from the About pages on every brand.
 *
 * Route: /why-we-build
 *
 * Design notes
 *   • Long-form editorial layout with a sticky TOC on desktop and a
 *     "Build your legacy while you are alive" banner as the central
 *     rallying line (per the founder's note).
 *   • Content is 100% verbatim from the founder's brief.
 *   • Keep components local — this page is intentionally self-
 *     contained so it can be forked into other brand deployments
 *     without pulling in unrelated dependencies.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles, Feather, Compass } from "lucide-react";
import SeoHead from "@/components/SeoHead";

/* ------------------------------------------------------------------ */
/*  Content model — every section is a plain object so the page       */
/*  reads top-to-bottom without deeply nested JSX.                    */
/* ------------------------------------------------------------------ */

const HERO = {
  overline: "The Founding Philosophy",
  title: "Why SocialPulse and SocialCapital.cc",
  titleAccent: "are being built",
  lead: [
    "Human beings have always wanted to be seen, understood and remembered.",
    "The desire takes many forms.",
  ],
  actions: [
    "We tell stories about ourselves.",
    "We share accomplishments.",
    "We display photographs of important moments.",
    "We take pictures when we travel.",
    "We treasure photographs with people we admire.",
    "We announce graduations, marriages, awards, promotions and achievements.",
    "We preserve family photographs.",
    "We place certificates on walls.",
    "We publish books.",
    "We build monuments.",
    "We create memorials.",
    "Today, we post our lives on social media.",
  ],
  closer: [
    "Not everyone seeks fame in the traditional sense, and many people value privacy deeply. But beneath these differences is a widespread human desire for something more fundamental:",
    "to know that our lives, relationships, work and contributions have meaning.",
    "We need recognition, belonging, respect, remembrance, and evidence that our life mattered.",
    "We want our families, friends, colleagues and communities to understand something about who we are and what we have done.",
    "Unfortunately, most people never create a structured record of that life.",
    "That is the problem SocialPulse and SocialCapital.cc are designed to address.",
  ],
};

const SECTIONS = [
  {
    slug: "historians",
    heading: "Famous People Have Historians. Who Documents Everyone Else?",
    body: [
      "When we think about Mahatma Gandhi, Martin Luther King Jr., Albert Einstein, Nelson Mandela, Mother Teresa and many other historic figures, we are not remembering them only because they once lived.",
      "We remember them because their lives were documented.",
    ],
    list: [
      "Journalists wrote about them.",
      "Photographers photographed them.",
      "Historians studied them.",
      "Organizations preserved their correspondence.",
      "Researchers recorded their achievements.",
      "Publishers printed their biographies.",
      "Schools taught future generations about them.",
      "Archives preserved their work.",
      "Documentary filmmakers retold their stories.",
    ],
    outro: [
      "In other words, their influence continued partly because society constructed a durable record around their lives.",
      "But history presents us with a question:",
    ],
    pull: "How many extraordinary people disappeared because nobody documented them?",
    list_after: [
      "Every village has had remarkable teachers.",
      "Every school has had students who later helped thousands of people.",
      "Every hospital has had nurses whose compassion changed lives.",
      "Every community has had volunteers who worked quietly for decades.",
      "Every family has had someone who sacrificed enormously for others.",
      "Every profession has had people who mentored generations without receiving public recognition.",
      "Every farm has knowledge passed from one generation to another.",
      "Every neighborhood contains people who routinely help others without asking for attention.",
    ],
    tail: [
      "They may never appear in newspapers.",
      "There may never be a biography written about them.",
      "Yet their contribution is real.",
      "SocialPulse begins with the belief that ordinary people should also have the opportunity to build a documented history of an extraordinary life.",
    ],
  },
  {
    slug: "vacations-vs-contributions",
    heading: "We Document Our Vacations Better Than Our Contributions",
    body: [
      "Modern technology has given us an extraordinary ability to document our lives.",
      "A person may have tens of thousands of photographs.",
      "We photograph:",
    ],
    list: [
      "vacations", "restaurants", "birthdays", "sunsets", "flowers",
      "family gatherings", "concerts", "celebrities", "airports",
      "landmarks", "and ourselves",
    ],
    outro: [
      "Our phones may know everywhere we traveled.",
      "Social networks may know whom we interacted with.",
      "Photo libraries may contain decades of memories.",
      "Yet we often fail to record:",
    ],
    list_after: [
      "whom we helped",
      "what we taught",
      "what knowledge we shared",
      "what organizations we supported",
      "what problems we solved",
      "what communities we strengthened",
      "what environment we protected",
      "what young people we mentored",
      "or what positive change we helped create",
    ],
    tail: [
      "This creates an unusual imbalance.",
      "We may possess a detailed record of where we went, while possessing almost no structured record of what we contributed.",
      "SocialPulse seeks to correct that imbalance.",
    ],
  },
  {
    slug: "not-a-feed",
    heading: "Your Life Should Not Be Reduced to a Social-Media Feed",
    body: [
      "Traditional social networks have made sharing easier than ever.",
      "But much of what we share has a very short digital life.",
      "A meaningful photograph may disappear down a feed within hours.",
      "A thoughtful article may be difficult to find months later.",
      "A community contribution may receive twenty likes and then effectively vanish.",
      "Years of participation become scattered across:",
    ],
    list: [
      "Facebook", "Instagram", "TikTok", "LinkedIn", "WhatsApp",
      "YouTube", "email", "school websites", "company systems",
      "club records", "and countless other places",
    ],
    outro: [
      "SocialPulse approaches personal contribution differently.",
      "Instead of asking only:",
    ],
    pull: "What are you posting today?",
    tail: [
      "we want to ask:",
      "What are you building over your lifetime?",
      "Every meaningful contribution should have the possibility of becoming part of an organized, searchable and enduring personal record.",
    ],
  },
  {
    slug: "attention-vs-capital",
    heading: "From Attention to Social Capital",
    body: [
      "There is an important difference between attention and reputation.",
      "Someone can receive enormous attention for a few minutes without creating lasting value.",
      "Social Capital should develop through something deeper:",
    ],
    list: [
      "relationships", "trust", "participation", "knowledge",
      "service", "mentoring", "leadership", "consistency", "and contribution",
    ],
    outro: ["This creates a progression:"],
    pull: "Action → Documentation → Verification → Impact → Social Capital → Reputation → Opportunity → Legacy",
    tail: [
      "A person's reputation should not need to depend entirely upon self-promotion.",
      "Their documented actions can speak for them.",
    ],
  },
  {
    slug: "small-actions",
    heading: "Social Capital Can Begin With Very Small Actions",
    body: [
      "A common mistake is to think that legacy belongs only to famous people.",
      "It does not.",
      "Legacy can begin with an ordinary Tuesday.",
    ],
    list: [
      "A student tutors another student.",
      "A person picks up litter during a morning walk.",
      "Someone helps an elderly neighbor learn how to use a phone.",
      "A traveler provides accurate information that helps hundreds of people.",
      "A retired professional mentors a young graduate.",
      "A gardener documents a plant species.",
      "A restaurant gives surplus food to a charity.",
      "A family records the memories of an elderly relative.",
      "A volunteer spends two hours helping at a school.",
    ],
    tail: [
      "None of these actions needs to make headlines.",
      "But repeated over five, ten, thirty or sixty years, they can create a powerful record.",
      "Social Capital should recognize the cumulative value of these actions.",
    ],
  },
  {
    slug: "socialpulse-role",
    heading: "SocialPulse: The Place Where Life and Community Are Documented",
    body: [
      "SocialPulse is intended to become the broad social and community layer of this ecosystem.",
      "People can create profiles, participate in communities, publish information, connect with others and document meaningful activity.",
      "Unlike a network focused primarily on entertainment or popularity, SocialPulse can encourage users to create a useful personal archive.",
      "Their profile can gradually show:",
    ],
    list: [
      "what they know",
      "what they care about",
      "where they contribute",
      "whom they help",
      "what communities they belong to",
      "what they create",
      "and how their contribution evolves over time",
    ],
    tail: ["SocialPulse becomes the social expression of that life."],
  },
  {
    slug: "socialcapital-role",
    heading: "SocialCapital.cc: The Record Behind the Story",
    body: [
      "SocialCapital.cc performs a different role.",
      "If SocialPulse helps tell the story, SocialCapital.cc helps organize the evidence behind it.",
      "SocialCapital.cc can receive qualifying activity from SocialPulse, sister sites and outside organizations and turn it into a unified Social Capital record.",
      "It can help answer:",
    ],
    list: [
      "What was the contribution?",
      "When did it happen?",
      "Which category does it belong to?",
      "Was it verified?",
      "How much effort was involved?",
      "What impact did it create?",
      "Was it reported elsewhere?",
      "Should it receive recognition?",
      "How does it contribute to the person's lifetime record?",
    ],
    tail: ["This is why SocialCapital.cc should remain the central Social Capital engine."],
  },
  {
    slug: "one-human",
    heading: "Many Interests. Many Sites. One Human Being.",
    body: [
      "Human lives cannot be neatly placed into one category.",
      "Someone may simultaneously be:",
    ],
    list: [
      "a parent", "teacher", "traveler", "gardener", "alumnus",
      "photographer", "mentor", "volunteer", "professional",
      "food enthusiast", "community member", "and environmental observer",
    ],
    tail: [
      "That is why the SocialPulse ecosystem includes specialized vertical platforms.",
      "Instead of forcing everything into one giant generic network, each vertical can help people document a particular aspect of life.",
      "Together they contribute to one broader story.",
    ],
  },
  {
    slug: "naturepulse",
    heading: "NaturePulse.net — Your Relationship With the Living Planet",
    body: ["NaturePulse can allow people to document:"],
    list: [
      "plants", "flowers", "trees", "birds", "animals", "weather",
      "clouds", "landscapes", "environmental change", "agriculture",
      "natural events", "and local conditions",
    ],
    tail: [
      "A casual photograph of a bird may become an observation.",
      "Thousands of observations over many years could become a valuable local environmental record.",
      "A member who participates in conservation, citizen science, tree planting or environmental education may build Environmental Social Capital.",
      "What began as personal interest becomes public knowledge.",
      "And public knowledge can become legacy.",
    ],
  },
  {
    slug: "farmerspulse",
    heading: "FarmersPulse.com — Preserving Agricultural Wisdom",
    body: [
      "Farmers carry knowledge that may never appear in textbooks.",
      "They understand:",
    ],
    list: [
      "soil", "rainfall", "planting cycles", "pests", "harvest patterns",
      "local crop varieties", "irrigation", "weather", "and food production",
    ],
    tail: [
      "Much of this knowledge has traditionally been transferred verbally.",
      "When one generation disappears, valuable knowledge can disappear with it.",
      "FarmersPulse can help farmers and agricultural communities document that wisdom.",
      "Sharing agricultural knowledge is not merely content creation.",
      "It can become a contribution to future generations.",
    ],
  },
  {
    slug: "foodpulse",
    heading: "FoodPulse.net — Turning Food Into Social Impact",
    body: [
      "Food is connected to nearly every human being.",
      "Yet enormous quantities of food are wasted while other people struggle to obtain enough to eat.",
      "FoodPulse can allow people, farms, restaurants, groceries, charities and volunteers to participate in a more intelligent food network.",
      "Members may contribute by:",
    ],
    list: [
      "sharing surplus",
      "rescuing food",
      "helping food banks",
      "teaching cooking skills",
      "reducing household waste",
      "sharing useful recipes",
      "documenting ingredients",
      "or helping communities understand food and nutrition",
    ],
    tail: [
      "Some of those actions create measurable community benefit.",
      "SocialCapital.cc can help recognize that contribution.",
    ],
  },
  {
    slug: "alumnipulse",
    heading: "AlumniPulse.net — Turning Experience Into Opportunity for Others",
    body: [
      "Education does not end at graduation.",
      "Former students carry decades of experience that can benefit those who come after them.",
      "An alumnus can:",
    ],
    list: [
      "mentor", "teach", "advise", "recommend", "connect", "sponsor",
      "provide internships", "help locate former classmates",
      "support institutions", "or preserve school history",
    ],
    tail: [
      "These activities create social value.",
      "AlumniPulse can help document them.",
      "SocialCapital.cc can help transform them into a lasting record of mentoring, education and institutional contribution.",
    ],
  },
  {
    slug: "travelerpulse",
    heading: "TravelerPulse.pro — Turning Experience Into Useful Knowledge",
    body: [
      "Travel creates experience.",
      "Experience can become knowledge.",
      "Knowledge can help someone else.",
      "A traveler who shares accurate information about:",
    ],
    list: [
      "airports", "transportation", "accessibility", "immigration procedures",
      "local customs", "safety", "hotels", "restaurants", "or destinations",
    ],
    tail: [
      "may save another traveler hours of confusion.",
      "Instead of every traveler repeatedly solving the same problems independently, TravelerPulse can allow experience to become reusable community knowledge.",
      "That too can be a contribution.",
    ],
  },
  {
    slug: "family-heritage",
    heading: "Family and Heritage Platforms — Giving Memory a Permanent Home",
    body: [
      "Families contain perhaps the largest undocumented archives in the world.",
    ],
    list: [
      "Stories exist in people's memories.",
      "Photographs remain in boxes.",
      "Recipes disappear.",
      "Migration histories are forgotten.",
      "Names vanish after several generations.",
      "Traditions disappear when nobody records them.",
    ],
    tail: [
      "Platforms such as FamilyTree.team and IndianFamilyTree.com can help people preserve these histories.",
      "Documenting your grandmother's life may not make you famous.",
      "But a descendant one hundred years from now may consider it priceless.",
      "Legacy does not require an audience of millions.",
      "Sometimes an audience of one future grandchild is enough.",
    ],
  },
  {
    slug: "geographic",
    heading: "Geographic Portals — Documenting the Story of Place",
    body: [
      "A person's identity is also connected to place.",
    ],
    list: [
      "Village.", "City.", "County.", "District.", "State.", "Province.", "Country.",
    ],
    outro: [
      "Geographic SocialPulse portals can help residents collectively document their communities.",
      "Instead of local history being written only decades later by historians, communities can continuously document:",
    ],
    list_after: [
      "people", "events", "organizations", "nature", "development",
      "problems", "businesses", "schools", "traditions", "and social change",
    ],
    tail: ["Residents become contributors to the historical archive of their own communities."],
  },
  {
    slug: "historically-valuable",
    heading: "You Do Not Have to Be Famous to Become Historically Valuable",
    body: [
      "This may be one of the most important ideas behind the entire project.",
      "History traditionally preserves the lives of presidents, kings, scientists, artists, religious leaders, athletes and celebrities.",
      "Digital technology gives us the opportunity to broaden history.",
      "Future generations should also be able to discover:",
    ],
    list: [
      "the village teacher",
      "the neighborhood gardener",
      "the immigrant entrepreneur",
      "the dedicated nurse",
      "the local photographer",
      "the farmer",
      "the engineer who mentored students",
      "the volunteer",
      "the parent who organized community activities",
      "and the person who quietly spent decades helping others",
    ],
    tail: ["SocialPulse can democratize the documentation of human contribution."],
  },
  {
    slug: "others-help",
    heading: "Document Yourself — But Let Others Help Tell the Story",
    body: [
      "There is sometimes discomfort around documenting one's own accomplishments.",
      "People may feel it sounds like boasting.",
      "But documentation and boasting are not the same thing.",
      "A factual record can simply say:",
    ],
    list: [
      "what happened",
      "when it happened",
      "who participated",
      "what impact occurred",
      "and what evidence exists",
    ],
    outro: [
      "Furthermore, SocialPulse can allow other people and organizations to contribute to someone's story.",
    ],
    list_after: [
      "A student can thank a teacher.",
      "A nonprofit can verify a volunteer.",
      "An alumnus can recognize a mentor.",
      "A family member can document an ancestor.",
      "A community can recognize a local contributor.",
    ],
    tail: [
      "In this way, reputation does not need to come entirely from self-description.",
      "It can become community-authored and evidence-supported.",
    ],
  },
  {
    slug: "opportunity-while-alive",
    heading: "Social Capital Can Create Opportunity While You Are Alive",
    body: [
      "Legacy is important, but the benefits should not begin after death.",
      "A strong Social Capital record may help people during life.",
      "For students it may demonstrate:",
    ],
    list: [
      "service", "leadership", "responsibility", "initiative", "and participation",
    ],
    outro: ["For job seekers it may demonstrate:"],
    list_after: [
      "mentoring", "teamwork", "community involvement",
      "knowledge sharing", "and consistency",
    ],
    tail: [
      "For professionals it can demonstrate contribution beyond job titles.",
      "For volunteers it can preserve years of service.",
      "For organizations it can provide evidence of impact.",
      "The goal is to turn positive participation into a portable reputation.",
    ],
  },
  {
    slug: "passport",
    heading: "The Social Capital Passport",
    body: [
      "Eventually a person may have hundreds or thousands of activities across multiple platforms.",
      "The Social Capital Passport can turn those records into a usable summary.",
      "Instead of simply saying:",
    ],
    pull: "\"I am involved in my community,\"",
    outro: ["a person may be able to show:"],
    list_after: [
      "12 years of documented service",
      "410 volunteer hours",
      "84 mentoring activities",
      "five participating organizations",
      "three environmental projects",
      "multiple verified badges",
      "and a collection of contributions",
    ],
    tail: [
      "That is a very different form of reputation.",
      "It is based less on claims and more on accumulated history.",
    ],
  },
  {
    slug: "recognition",
    heading: "Recognition Can Encourage More Contribution",
    body: [
      "People respond to recognition.",
      "This is not necessarily vanity.",
      "Recognition tells someone:",
    ],
    pull: "Your effort mattered.",
    list: [
      "A badge.",
      "A thank-you.",
      "A milestone.",
      "A certificate.",
      "A congratulatory message.",
      "A Social Capital increase.",
      "A story published about someone's work.",
    ],
    outro: [
      "Each can reinforce positive behavior.",
      "SocialPulse can therefore redirect some of the psychological power that existing social networks use for likes and followers toward:",
    ],
    list_after: [
      "service", "learning", "mentoring", "environmental stewardship",
      "community participation", "and knowledge",
    ],
    tail: ["That is a profound opportunity."],
  },
  {
    slug: "self-competition",
    heading: "Instead of Competing for Attention, Compete With Yourself to Contribute",
    body: [
      "Social Capital should avoid becoming a contest where people compare human worth.",
      "The healthiest comparison is with your own history.",
    ],
    list: [
      "Last year: 20 volunteer hours. This year: more.",
      "Last year: five students mentored. This year: eight.",
      "Last year: 25 environmental observations. This year: more.",
    ],
    tail: [
      "The system can encourage personal growth without declaring that one person is \"better\" than another.",
    ],
  },
  {
    slug: "organizations",
    heading: "Organizations Also Need a Legacy",
    body: ["The concept does not apply only to individuals."],
    list: [
      "Schools have legacies.",
      "Businesses have legacies.",
      "Nonprofits have legacies.",
      "Clubs have legacies.",
      "Villages have legacies.",
      "Universities have legacies.",
    ],
    outro: [
      "An organization may exist for 100 years but retain surprisingly little structured information about its cumulative contribution.",
      "SocialCapital.cc can help organizations maintain a history of:",
    ],
    list_after: [
      "participants", "projects", "volunteer hours", "community impact",
      "environmental work", "mentoring", "education", "and achievements",
    ],
    tail: [
      "Organizations can therefore build Social Impact records alongside individual Social Capital records.",
    ],
  },
  {
    slug: "not-lost",
    heading: "A Network Where Good Activity Is Not Lost",
    body: ["The architecture can ultimately work like this:"],
    flow: [
      "A deed occurs.",
      "It is documented on the most appropriate platform.",
      "SocialCapital.cc evaluates qualifying contribution.",
      "Evidence and verification strengthen the record.",
      "Social Capital is added to the person's lifetime history.",
      "SocialPulse allows the community to see and interact with it where authorized.",
      "The contribution becomes part of a Passport.",
      "Years of contributions become a reputation.",
      "A lifetime of contributions becomes a legacy.",
    ],
    tail: [
      "This is fundamentally different from a feed designed around what is new.",
      "It is a system designed around what is worth remembering.",
    ],
  },
  {
    slug: "reach-future",
    heading: "Your Legacy Can Reach People You Will Never Know",
    body: [
      "When people think about legacy, they often think about their children and grandchildren.",
      "But a meaningful record can reach much further.",
    ],
    list: [
      "A future student may discover an article you wrote.",
      "A researcher may benefit from environmental observations you documented decades earlier.",
      "A descendant may discover a photograph of an ancestor.",
      "A young professional may learn from advice you recorded.",
      "A community may learn how its neighborhood changed.",
      "A future farmer may rediscover a local agricultural technique.",
    ],
    tail: [
      "You may never meet any of these people.",
      "Yet your contribution may still help them.",
      "That may be one of the deepest forms of Social Capital.",
    ],
  },
  {
    slug: "use-what-i-leave",
    heading: "From \"Remember Me\" to \"Use What I Leave Behind\"",
    body: [
      "The highest form of legacy may not be fame.",
      "It may be usefulness.",
      "Instead of merely saying:",
    ],
    pull: "Remember my name,",
    outro: ["a person can leave behind something more meaningful:"],
    list_after: [
      "Here is what I learned.",
      "Here is what I observed.",
      "Here is whom I helped.",
      "Here is what worked.",
      "Here are my mistakes.",
      "Here are my photographs.",
      "Here is my knowledge.",
      "Here is my contribution.",
      "Use it if it can help you.",
    ],
    tail: ["That is the philosophy SocialPulse can help cultivate."],
  },
  {
    slug: "during-life",
    heading: "A Digital Legacy Should Be Built During Life — Not After Death",
    banner: true,   // this is the central rallying line per the founder note
    body: [
      "Most memorials begin after someone dies.",
      "At that point, family and friends struggle to reconstruct an entire life from fragments.",
    ],
    list: [
      "Names are forgotten.",
      "Dates are uncertain.",
      "Photographs cannot be identified.",
      "Stories disappear with the people who knew them.",
    ],
    outro: ["SocialPulse proposes a fundamentally different approach:"],
    pull: "Build your legacy while you are alive.",
    list_after: [
      "Add to it continuously.",
      "Correct it.",
      "Explain your photographs.",
      "Tell your stories.",
      "Record your accomplishments.",
      "Acknowledge people who helped you.",
      "Document what you learned.",
      "Share your observations.",
      "Preserve your work.",
      "Decide what should remain private and what future generations may see.",
    ],
    tail: [
      "A person's Legacy Profile can therefore be largely authored by the person who actually lived the life.",
    ],
  },
  {
    slug: "platform-for-purpose",
    heading: "SocialPulse Can Become a Platform for Purpose",
    body: [
      "The long-term opportunity is larger than creating another website.",
      "SocialPulse can help create an ecosystem around:",
    ],
    list: [
      "Identity", "Connection", "Contribution", "Recognition",
      "Knowledge", "Reputation", "Impact", "History", "and Legacy",
    ],
    tail: [
      "Each vertical site captures a different dimension of human activity.",
      "SocialCapital.cc brings qualifying contribution together.",
      "SocialPulse connects the people.",
      "The Social Capital Passport makes the record portable.",
      "Legacy preserves what matters.",
      "Together they create a new type of social platform.",
    ],
  },
  {
    slug: "the-question",
    heading: "The Question We Should Ask Everyone",
    body: ["Most social networks ask:"],
    pull: "What's on your mind?",
    outro: ["SocialPulse can ask something deeper:"],
    pull_after: "What did you contribute today?",
    tail: [
      "And over time:",
      "What have you contributed throughout your life?",
      "The answer may begin with one photograph, one article, one hour of volunteering or one person helped.",
      "But decades later, those records can tell the story of an entire human life.",
      "Not simply where you traveled.",
      "Not simply what you purchased.",
      "Not simply whom you met.",
      "But:",
    ],
    list_after: [
      "what you learned",
      "what you shared",
      "what you created",
      "whom you helped",
      "what you protected",
      "and what you left behind",
    ],
  },
  {
    slug: "every-person",
    heading: "Every Person Has a Story Worth Preserving",
    body: [
      "Most of us will never become Gandhi, Einstein, Mandela, Martin Luther King Jr. or Mother Teresa.",
      "That does not mean our lives lack historical or social value.",
      "A person does not have to change the entire world.",
    ],
    list: [
      "Changing one person's world matters.",
      "Teaching one child matters.",
      "Planting one tree matters.",
      "Preserving one family story matters.",
      "Helping one neighbor matters.",
      "Sharing one useful piece of knowledge matters.",
    ],
    tail: [
      "And when millions of people document millions of such contributions, something extraordinary becomes possible.",
      "We begin creating not merely another social network, but a living record of human contribution.",
      "That is the larger vision behind SocialPulse and SocialCapital.cc.",
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Small presentational helpers                                       */
/* ------------------------------------------------------------------ */

const Lines = ({ items, muted = false }) =>
  items.map((t, i) => (
    <p
      key={i}
      className="text-[17px] sm:text-lg leading-[1.75]"
      style={{ color: muted ? "var(--wofy-text-soft, #4b5563)" : "var(--wofy-text, #111827)" }}
    >
      {t}
    </p>
  ));

const InlineList = ({ items, testid }) => (
  <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1.5" data-testid={testid}>
    {items.map((li, i) => (
      <li key={i} className="flex items-start gap-3 text-[16px] leading-relaxed">
        <span
          className="mt-2 w-1.5 h-1.5 rounded-full shrink-0"
          style={{ background: "#059669" }}
        />
        <span>{li}</span>
      </li>
    ))}
  </ul>
);

const PullQuote = ({ children }) => (
  <blockquote
    className="my-6 pl-5 border-l-4 italic text-xl sm:text-2xl font-display leading-snug"
    style={{ borderColor: "#10b981", color: "#0f172a" }}
  >
    {children}
  </blockquote>
);

const FlowStep = ({ text, last }) => (
  <div className="flex flex-col items-start gap-1">
    <div
      className="rounded-xl px-4 py-2.5 text-[15px] font-medium bg-white border shadow-sm"
      style={{ borderColor: "rgba(5,150,105,0.25)", color: "#065f46" }}
    >
      {text}
    </div>
    {!last && (
      <div className="ml-4 h-4 w-px" style={{ background: "#10b981" }} />
    )}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export default function WhyWeBuildPage() {
  return (
    <div
      className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12 md:py-16"
      data-testid="why-we-build-page"
      style={{ color: "var(--wofy-text, #111827)" }}
    >
      <SeoHead
        section="about"
        title="Why SocialPulse and SocialCapital.cc are being built"
        description="A long-form manifesto: how documented contribution — not attention — becomes a lifelong social capital record, and the philosophy behind the SocialPulse ecosystem."
        url="/why-we-build"
      />

      {/* Back nav */}
      <Link
        to="/vision-mission"
        data-testid="back-to-about"
        className="inline-flex items-center gap-2 text-sm mb-8 hover:underline underline-offset-4"
        style={{ color: "var(--wofy-text-soft, #4b5563)" }}
      >
        <ArrowLeft className="w-4 h-4" /> Back to About
      </Link>

      {/* HERO */}
      <header className="mb-16">
        <div
          className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-mono uppercase tracking-widest"
          style={{ borderColor: "rgba(5,150,105,0.4)", color: "#059669", background: "rgba(236,253,245,0.6)" }}
        >
          <Sparkles className="w-3 h-3" /> {HERO.overline}
        </div>
        <h1
          className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4"
          style={{ color: "#0f172a" }}
        >
          {HERO.title}{" "}
          <span style={{ color: "#059669" }}>{HERO.titleAccent}.</span>
        </h1>
        <div className="mt-8 space-y-3 max-w-3xl">
          <Lines items={HERO.lead} />
        </div>
        <InlineList items={HERO.actions} testid="hero-actions" />
        <div className="mt-6 space-y-3 max-w-3xl">
          <Lines items={HERO.closer} muted />
        </div>
      </header>

      {/* Central banner — the founder's rallying line */}
      <aside
        className="relative overflow-hidden rounded-3xl p-8 md:p-12 mb-16"
        style={{
          background: "linear-gradient(135deg, #065f46 0%, #059669 55%, #10b981 100%)",
          color: "#F0FDF4",
        }}
        data-testid="rallying-banner"
      >
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-25 pointer-events-none"
             style={{ background: "radial-gradient(circle, #A7F3D0 0%, transparent 70%)" }} />
        <div className="overline text-white/70">The Central Idea</div>
        <h2 className="font-display text-3xl md:text-5xl mt-3 leading-tight max-w-3xl">
          Build your legacy <span className="italic text-white/90">while you are alive</span>.
        </h2>
        <p className="mt-4 max-w-2xl text-white/90 text-base sm:text-lg leading-relaxed">
          Not as an appeal to vanity — as an invitation to preserve knowledge,
          service, experience and contribution for the relatives, communities
          and complete strangers who will come after you.
        </p>
      </aside>

      {/* BODY with sticky TOC */}
      <div className="grid lg:grid-cols-12 gap-8 md:gap-12">
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-[260px]">
            <div className="overline mb-3 flex items-center gap-2"
                 style={{ color: "#059669" }}>
              <Compass className="w-3 h-3" /> Contents
            </div>
            <nav className="flex flex-col gap-1.5">
              {SECTIONS.map((s) => (
                <a
                  key={s.slug}
                  href={`#${s.slug}`}
                  data-testid={`toc-${s.slug}`}
                  className="text-sm leading-snug hover:underline underline-offset-4"
                  style={{ color: "var(--wofy-text-soft, #4b5563)" }}
                >
                  {s.heading}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        <div className="lg:col-span-9 space-y-16 md:space-y-20">
          {SECTIONS.map((sec) => (
            <section
              key={sec.slug}
              id={sec.slug}
              className="scroll-mt-[260px]"
              data-testid={`section-${sec.slug}`}
            >
              <h2
                className="font-display text-2xl md:text-3xl leading-tight"
                style={{ color: sec.banner ? "#059669" : "#0f172a" }}
              >
                {sec.heading}
              </h2>

              <div className="mt-5 space-y-3">
                {sec.body && <Lines items={sec.body} />}
                {sec.pull && <PullQuote>{sec.pull}</PullQuote>}
                {sec.list && <InlineList items={sec.list} testid={`list-${sec.slug}`} />}
                {sec.outro && <div className="mt-4 space-y-3"><Lines items={sec.outro} muted /></div>}
                {sec.pull_after && <PullQuote>{sec.pull_after}</PullQuote>}
                {sec.list_after && <InlineList items={sec.list_after} testid={`list2-${sec.slug}`} />}

                {sec.flow && (
                  <div className="mt-6" data-testid={`flow-${sec.slug}`}>
                    {sec.flow.map((step, i) => (
                      <FlowStep key={i} text={step} last={i === sec.flow.length - 1} />
                    ))}
                  </div>
                )}

                {sec.tail && <div className="mt-4 space-y-3"><Lines items={sec.tail} /></div>}
              </div>
            </section>
          ))}
        </div>
      </div>

      {/* CLOSING CTA */}
      <aside
        className="mt-20 rounded-3xl p-8 md:p-12 relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          color: "#F8FAFC",
        }}
        data-testid="why-cta"
      >
        <div className="overline text-white/60">
          <Feather className="w-3 h-3 inline mr-1" /> The invitation
        </div>
        <h3 className="font-display text-3xl md:text-4xl mt-3 max-w-3xl leading-tight">
          Every Life Creates a Story. Every Contribution Creates Value.
        </h3>
        <p className="mt-4 max-w-3xl text-white/80 text-base sm:text-lg leading-relaxed">
          Every Good Deed Can Build Social Capital. Every Person Can Leave a Legacy.
        </p>
        <div className="mt-4 text-white/60 text-sm font-mono uppercase tracking-widest">
          SocialCapital™ — Document What You Do · Measure the Good You Create · Build a Reputation · Leave a Legacy
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/social-capital"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium"
            style={{ background: "#10b981", color: "#052e16" }}
            data-testid="cta-start-earning"
          >
            <Sparkles className="w-4 h-4" /> Start earning social capital
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium border border-white/25 text-white hover:bg-white/10 transition-colors"
            data-testid="cta-create-account"
          >
            Create account <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-6 text-xs text-white/50">
          SocialCapital.cc — Powered by Social Pulse®
        </div>
      </aside>
    </div>
  );
}
